import React from 'react';
import {
  Box,
  Container,
  Typography,
  IconButton,
  Card,
  CardContent,
  Chip,
  Divider,
} from '@mui/material';
import {
  ArrowBack,
  NewReleases,
  FiberNew,
  Upgrade,
  BugReport,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const categoryConfig = {
  New: { color: 'success', icon: <FiberNew sx={{ fontSize: 16 }} /> },
  Improved: { color: 'info', icon: <Upgrade sx={{ fontSize: 16 }} /> },
  Fixed: { color: 'warning', icon: <BugReport sx={{ fontSize: 16 }} /> },
};

const releases = [
  {
    version: '2.1.0',
    date: 'March 2026',
    summary: 'AI-powered engineering workflows and improved onboarding',
    changes: [
      { text: 'AI-powered CAD drawing generation via Drawing Agent with iterative refinement', category: 'New' },
      { text: 'Role-based access management panel for admins in Settings', category: 'New' },
      { text: 'Audit log tracking for all platform actions with user, timestamp, and resource details', category: 'New' },
      { text: 'WelcomeDialog onboarding flow for first-time users with software dock selection', category: 'New' },
      { text: 'AI document generation from CAD files (PQP, user manuals, verification sheets)', category: 'New' },
      { text: 'Expanded railway knowledge base: 25 rail profiles, 16 fastenings, 15 turnouts, 10 gauges', category: 'Improved' },
      { text: 'Upgraded AI model with 16K token context for richer, more detailed outputs', category: 'Improved' },
      { text: 'Help Center and Changelog pages for better platform documentation', category: 'New' },
    ],
  },
  {
    version: '2.0.0',
    date: 'February 2026',
    summary: 'Enterprise-grade security and authentication overhaul',
    changes: [
      { text: 'Web application firewall protection', category: 'New' },
      { text: 'Threat detection for infrastructure monitoring', category: 'New' },
      { text: 'Multi-Factor Authentication (MFA) with TOTP and recovery codes', category: 'New' },
      { text: 'Role-Based Access Control (RBAC) with Engineer, Manager, and Admin roles', category: 'New' },
      { text: 'Enterprise-grade authentication for production environments', category: 'Improved' },
      { text: 'Custom domain with CloudFront distribution and SSL certificates', category: 'New' },
      { text: 'Secure credential storage with encrypted secrets management', category: 'Improved' },
      { text: 'Production validation checks at startup (CORS, auth mode, HTTPS enforcement)', category: 'Improved' },
      { text: 'Account lockout after failed login attempts with configurable duration', category: 'New' },
      { text: 'CORS origin validation rejecting wildcards in production environments', category: 'Fixed' },
    ],
  },
  {
    version: '1.0.0',
    date: 'January 2026',
    summary: 'Initial release of the EIS Agent Platform',
    changes: [
      { text: 'Project management with groups, systems, subsystems, and parts hierarchy', category: 'New' },
      { text: 'Git-style version control: branches, staging, commits, merge requests, and conflict resolution', category: 'New' },
      { text: 'CAD Viewer for 2D/3D model visualization', category: 'New' },
      { text: 'AI Assistant (e-pilot) powered by advanced language models', category: 'New' },
      { text: 'Document management with versioning, metadata extraction, and file comparison', category: 'New' },
      { text: 'Requirements tracking linked to parts and verification methods', category: 'New' },
      { text: 'Engineering Change Requests (ECR) and Engineering Change Orders (ECO) workflow', category: 'New' },
      { text: 'BOM (Bill of Materials) management with mass, cost, and CO2 rollup tracking', category: 'New' },
      { text: 'Simulation management with structured geometry, boundary conditions, and results', category: 'New' },
      { text: 'Multi-language support (English, Spanish, French, German, Arabic, Chinese)', category: 'New' },
    ],
  },
];

const ChangelogPage = () => {
  const navigate = useNavigate();

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <IconButton
          onClick={() => navigate(-1)}
          sx={{ mr: 2 }}
          aria-label="Go back"
        >
          <ArrowBack />
        </IconButton>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Changelog
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Release notes and platform updates
          </Typography>
        </Box>
      </Box>

      {/* Release Entries */}
      <Box sx={{ position: 'relative' }}>
        {/* Timeline Line */}
        <Box
          sx={{
            position: 'absolute',
            left: 20,
            top: 0,
            bottom: 0,
            width: 2,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: { xs: 'none', sm: 'block' },
          }}
        />

        {releases.map((release, index) => (
          <Box
            key={release.version}
            sx={{
              display: 'flex',
              mb: 4,
              position: 'relative',
            }}
          >
            {/* Timeline Dot */}
            <Box
              sx={{
                display: { xs: 'none', sm: 'flex' },
                alignItems: 'flex-start',
                pt: 2.5,
                mr: 3,
                zIndex: 1,
              }}
            >
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  boxShadow: 2,
                }}
              >
                <NewReleases sx={{ fontSize: 22 }} />
              </Box>
            </Box>

            {/* Release Card */}
            <Card
              sx={{
                flex: 1,
                boxShadow: 2,
                borderRadius: 2,
                overflow: 'visible',
                border: index === 0 ? '2px solid' : 'none',
                borderColor: index === 0 ? '#667eea' : 'transparent',
              }}
            >
              <CardContent sx={{ p: 3 }}>
                {/* Version Header */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                    mb: 1.5,
                  }}
                >
                  <Chip
                    label={`v${release.version}`}
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.9rem',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: '#fff',
                    }}
                  />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 500 }}
                  >
                    {release.date}
                  </Typography>
                  {index === 0 && (
                    <Chip
                      label="Latest"
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  )}
                </Box>

                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, mb: 2 }}
                >
                  {release.summary}
                </Typography>

                <Divider sx={{ mb: 2 }} />

                {/* Changes List */}
                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                  {release.changes.map((change, changeIndex) => {
                    const config = categoryConfig[change.category];
                    return (
                      <Box
                        component="li"
                        key={changeIndex}
                        sx={{
                          mb: 1,
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1,
                          listStyle: 'none',
                        }}
                      >
                        <Chip
                          label={change.category}
                          size="small"
                          color={config.color}
                          icon={config.icon}
                          variant="outlined"
                          sx={{
                            mt: 0.2,
                            minWidth: 90,
                            '& .MuiChip-label': { fontSize: '0.7rem' },
                          }}
                        />
                        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                          {change.text}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>
    </Container>
  );
};

export default ChangelogPage;
